import { 
    IDictionary,
    NestedArray,
    IBackoffStrategy,
    DecorrelatedBackoffStrategy
} from '../domain';

interface syncPromiseValidator<T>{
    (val:T,i:number): boolean|void
}
interface PromiseProducer<T> extends Function {
    (...args:any[]): ng.IPromise<T>;
}

export class CommonUtilsService {
    public static readonly $inject = [
        '$timeout',
        '$q'
    ];

    /**
     * Get a module if it already exists or create one with the specified depedencies if it doesn't
     */
    public static tryGetModule = (moduleName:string, deps:string[] = []) => {
       try {
           return angular.module(moduleName);
       }
       catch(e){
           return angular.module(moduleName, deps);
       }
    };

    /**
     * Convert an array-like object to a proper array.
     * This works on things like the arguments implicit function
     * variable and other iterable sources.
     */
    public static toArray<T>(collection:ArrayLike<T>){
        return Array.prototype.slice.call(collection) as T[]; 
    }

    /**
    * Searches an array in logarithmic time given a comparison predicate.
    * The array must be sorted for the algorithm to produce correct results.
    * 
    * @param arr: A sorted array to recursively search.
    * @param comparator: A function to determine if the desired element occurs 
    * before or after a given element in the array. Returing -1 indicates before, 
    * returning 1 indicates after, and returning 0 indicates equivalent to.
    * @param returnClosest: Returns the last remaining element if no explicit match.
    */
   public static binarySearch<T>(arr:T[], comparator:(elem:T) => -1|0|1, returnClosest = false):T{
        if(arr.length === 1){
            return arr[0];
        }

        // logically "slice" the array inline using start and end indices
        let startIndex = 0;
        let endIndex = arr.length - 1;

        while(startIndex <= endIndex){
            // no result found, but consumer wanted closest
            if(startIndex === endIndex && returnClosest){
                return arr[startIndex];
            }

            let midIndex = Math.ceil((endIndex + startIndex)/2);
            let midElement = arr[midIndex];
            let compareResult = comparator(midElement);   

            if(compareResult === 0){
                return midElement;
            }
            // result is left of midpoint
            else if(compareResult < 0){
                endIndex = midIndex - 1;
            }
            // result right of midpoint
            else {
                startIndex = midIndex + 1;            
            }
        }
    }
   

    constructor(
        private readonly $timeout: ng.ITimeoutService, 
        private readonly $q: ng.IQService
    ){
    }

    /**
     * Returns a function, that, as long as it continues to be invoked within the specified period of time,
     * will not be triggered. The function will be called after it stops being called for `wait` milliseconds.
     * The function will return a promise that will be resolved with the result of the original function once
     * it has been invoked. If the function was called multiple times within `wait` milliseconds, the same promise
     * will be returned, so each caller will have a promise that will resolve with the eventual result of the original function.
     * 
     * @param func The function to debounce.
     * @param wait The number of milliseconds to wait before `func` gets invoked.
     * @param context An optional context to apply to the call of `func`.
     * @returns A debounced version of `func`.
     * 
     * Adapted from https://github.com/jashkenas/underscore/blob/master/underscore.js#L876-L907
     */
    public debounce<T extends PromiseProducer<R>, R>(func:T, wait:number = 10, context?:any):T {
        let $timeout = this.$timeout;
        let $q = this.$q;

        let timer;
        let p:ng.IDeferred<R>;

        const resetDebounceState = () => {
            timer = undefined;
            p = undefined;
        };

        // we need to temporarily cast this to 'any' to allow casting back to T later
        let debounced:any = (...args: any[]) => {
            context = context || this;
            
            // If multiple calls to this function happen within `wait` milliseconds, only the last call should
            // actually be invoked.

            if (!p) {
                // reset promise
                p = $q.defer();
            }
            
            if (timer) {
                // Function is being called within the debounce threshhold. Cancel the current invocation that
                // has been queued and restart the timer.
                $timeout.cancel(timer);
            }

            // queue up invocation of original function here.
            timer = $timeout(() => {
                // Get a local variable to the current promise here
                // so that resetting it doesn't interfere with this function
                let scoped = p;
                let debouncedResult:ng.IPromise<R> = func.apply(context, args);
                scoped.resolve(debouncedResult);
            }, wait);
            
            return p.promise.finally(resetDebounceState);
        }
        return debounced as T;
    }

    public propsToCamel<T>(obj: T): T {
        var valType = typeof(obj);
        var isPrimitive = valType === 'boolean' || valType === 'number' || valType === 'string';
        if (angular.isArray(obj)) {
            ((obj as any) as any[]).forEach(el => this.propsToCamel(el));
        } else if(!isPrimitive && !!obj) {
            // cache the keys to avoid infinite recursion while updating
            var keys = Object.keys(obj);
            keys.forEach(k => { 
                var val = obj[k];
                obj[k] = null;
                delete obj[k];
                var chars = k.split('');
                chars[0] = k[0].toLowerCase();
                k = chars.join('');
                val = this.propsToCamel(val);
                obj[k] = val;         
            });
        }

        return obj;
    }

    public arrayToDict<T>(arr:T[], keyPredicate:(item:T) => string): IDictionary<T> {
        return arr.reduce((all, item) => {
            let propertyKey = keyPredicate(item);
            if(angular.isDefined(propertyKey)){
                all[propertyKey] = item;
            }

            return all;
        }, {}) as IDictionary<T>;
    }

    public dictToArray<T>(dict:IDictionary<T>){
        return Object.keys(dict).map(k => dict[k]);
    }

    public generateUid() {
        return ((Date.now() + Math.random()) * Math.random()).toString().replace('.', '');
    }

    public generateHash(str:string){
        var hash = 0;
        var chr;
       
        for (var i = 0; i < str.length; i++) {
            chr = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + chr;
            hash |= 0; // Convert to 32bit integer
        }
        
        return hash.toString();
    }

    /**
     * Recursively flatten an array of arrays.
     * @param arr An array of one-plus-dimensional arrays.
     */
    public flatten<T>(arr:NestedArray<T>):T[] {
        return arr.reduce<T[]>((all, curr) => {
            let flattened = Array.isArray(curr) ? this.flatten(curr) : curr;
            return all.concat(flattened);
        }, []);
    }

    public resolveSync<T>(promises:ng.IPromise<T>[]|PromiseProducer<T>[], onEach?:syncPromiseValidator<T>):ng.IPromise<T[]>{
        let results:T[] = [];
        let $q = this.$q;
        let accumulator = 0;
        return resolvePromises(promises).then(() => results);

        function resolvePromises(remaining:ng.IPromise<T>[]|PromiseProducer<T>[]) {   
            let next = remaining.shift();
            let p:ng.IPromise<T> = angular.isFunction(next) ? 
                (next as PromiseProducer<T>)() : 
                (next as ng.IPromise<T>);
            if(p){                
                return p.then<T>(val => {
                    let continueProcessing:boolean|void = true;                    
                    
                    if(angular.isFunction(onEach)){
                        continueProcessing = onEach(val, accumulator);
                        if(!angular.isDefined(continueProcessing)){
                            continueProcessing = true;
                        }
                    }                    
                    
                    if(continueProcessing){    
                        results.push(val);                    
                        accumulator++;
                        return resolvePromises(remaining);
                    }
                });
            } 
            else if(remaining.length === 0) {
                return $q.resolve(); 
            }
        }
    }

    public htmlEncode(text:string):string {
        const escapedCharsRegex = /[\u00A0-\u9999\<\>\&\'\"]/gim;
        return text.toString().replace(escapedCharsRegex, c => '&#' + c.charCodeAt(0) + ';');
    }

    public htmlDecode(text:string):string {
        // https://regex101.com/r/F5C7tO/1/
        const escapedCharsRegex = /\&\#(\d+)\;/gim;
        return text.toString().replace(escapedCharsRegex, (g1, g2) =>  String.fromCharCode(+g2));
    }

    public interpolationEscape(text:string):string{
        // https://docs.angularjs.org/api/ng/service/$interpolate#escaped-interpolation
        return text.toString().replace('{{', '\\{\\{').replace('}}', '\\}\\}');
    }

    /**
     * Filters an array based on async criteria
     * @param array An array of items of a generic type.
     * @param callback A function that takes a value and index in the array and returns a boolean promise if it should kept in the array.
     * @returns A promise with a payload that is the filtered array.
     */
    public filterAsync<T>(array: T[], callback:(value:T, index:number) => ng.IPromise<boolean>) {
        let filterCriteria = this.$q.all(array.map((arrayVal, arrayIndex) => callback(arrayVal, arrayIndex)));

        return filterCriteria
            .then(filterResults => array.filter((val, i) => filterResults[i]));
    }

    /**
     * Executes the specified function and retries if it throws after delayInMs milliseconds.
     * Subsequent retries use a "decorrelated jitter" (https://aws.amazon.com/blogs/architecture/exponential-backoff-and-jitter/)
     * algorithem up to the specified maxDelayInMs milliseconds.
     * This uses the ng.$timeout service and the provided setCurrentTimeout callback will be invoked
     * with each invocation in order to allow the consumer to have a handle to the timeout promise to
     * cancel it if necessary.
     * @param fn The action to execute and retry upon failure.
     * @param delayInMs The initial retry delay in milliseconds.
     * @param maxDelayInMs The maximum delay between retries the backoff algorithm can reach.
     * @param setCurrentTimeout A callback which will be invoked with the current $timeout ng.IPromise for each retry.
     */
    public retryWithBackoff(fn: () => ng.IPromise<void>,
        baseDelayInMs: number,
        maxDelayInMs: number,
        setCurrentTimeout: (timeoutPromise: ng.IPromise<void>) => void): void {

        retryWithBackoff(fn, baseDelayInMs, maxDelayInMs, new DecorrelatedBackoffStrategy(baseDelayInMs, maxDelayInMs), setCurrentTimeout, 1);

        const $timeout = this.$timeout;
        function retryWithBackoff(fn: () => ng.IPromise<void>,
            baseDelayInMs: number,
            maxDelayInMs: number,
            backoffStrategy: IBackoffStrategy,
            setCurrentTimeout: (timeoutPromise: ng.IPromise<void>) => void,
            attemptNumber: number) {

            fn().catch(() => {
                setCurrentTimeout($timeout(() => retryWithBackoff(fn, baseDelayInMs, maxDelayInMs, backoffStrategy, setCurrentTimeout, attemptNumber + 1), backoffStrategy.backoff(attemptNumber)));
            });
        }
    }
}


// WEBPACK FOOTER //
// ./src/util/services/commonUtilsService.ts